import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import AddendumSignedIcon from "@/components/AddendumSignedIcon";
import Flex from "@/components/Flex";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../components/RenderWithTooltip";
import Text from "../../../../components/Text";
import ActionMenu from "../../../../components/app/ActionMenu";
import { BILLING_TABLE_ROW_HEIGHT } from "../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../constants/paths";
import { AppointmentSchedulingType } from "../../../../dummy-data/appointment-list-data-scheduling";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { useAppointmentsV2 } from "../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { formatDateTime } from "../../../../utils/date-time-utility";
import { PatientName } from "../../../../utils/util-components/UtilComponents";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  getFormattedId,
  getXtenantId,
  roleFilter,
} from "../../../../utils/utils";
import { FilterData } from "./BillingsFilter";
import { BILLED_STATUS_MAPPER, BilledStatus } from "./billing-constant";
import { SUPERBILLPLACEOFSERVICEMAPPER } from "./add-super-bill-const";
import CreateClaimDrawer from "./claims/CreateClaimDrawer";
import ViewEncounterSummaryDrawer from "./encounter/ViewEncounterSummaryDrawer";

type OutletContextType = {
  filter: FilterData;
  searchKey: string;
};

const ReadyForBillingTab = () => {
  const { filter, searchKey } = useOutletContext<OutletContextType>();
  const [addClaimsDisclosure] = useReduceDisclosure(2);

  const viewEncounterDisclosure = useDisclosure();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const { hasView, hasReadOnly: hasReadOnlyEncounterBilling } =
    useRoleController("Encounter for billing");
  const { hasUpdate: hasUpdateSuperbill } =
    useRoleController("Generate Superbill");
  const { hasUpdate: hasUpdateClaims } = useRoleController("Generate Claims");

  const {
    data: appointmentsList,
    isLoading,
    isFetching,
  } = useAppointmentsV2({
    pagination,
    gridSorting,
    providerGroupId: getXtenantId(),
    appointmentStatus: ["COMPLETED"],
    providerUuid: (filter?.provider_Name as any)?.value ?? undefined,
    appointmentType: filter?.appointment_Type?.value
      ? [filter.appointment_Type.value]
      : undefined,
    searchKey,
    // superBillGenerated: false,
    billedStatus: filter?.billed_Status?.length
      ? filter.billed_Status.map((it) => it.value).join(",")
      : undefined,
    isAddendumSigned: filter?.addendum_Signed?.value
      ? filter.addendum_Signed.value === "true"
      : undefined,
    dateRangeType: filter?.date_range ?? undefined,
    startDate:
      filter?.date_range === "CUSTOM_RANGE"
        ? filter?.startDate
          ? dayjs(filter?.startDate).toISOString()
          : undefined
        : undefined,
    endDate:
      filter?.date_range === "CUSTOM_RANGE"
        ? filter?.endDate
          ? dayjs(filter?.endDate).endOf("day").toISOString()
          : undefined
        : undefined,
  });

  const COLUMNS = applyDefaultColumnSettings<AppointmentSchedulingType>(
    [
      {
        field: "uuid",
        flex: 0.7,
        minWidth: 120,
        headerName: "Visit ID",
        sortable: false,
        renderCell: ({ row }) => {
          if (!row?.uuid) return <Text isTabletext>-</Text>;
          return (
            <Flex alignItems="center" gap={1}>
              {hasView ? (
                <Text
                  isTabletext
                  sx={{ cursor: "pointer", color: "primary.main" }}
                  onClick={() => viewEncounterDisclosure.open(row.uuid)}
                >
                  {getFormattedId(row.uuid)}
                </Text>
              ) : (
                <Text isTabletext>{getFormattedId(row.uuid)}</Text>
              )}
              {row?.addendumSigned && <AddendumSignedIcon />}
            </Flex>
          );
        },
      },
      {
        field: "appointmentDate",
        flex: 1,
        minWidth: 170,
        headerName: "Visit Date & Time",
        sortable: false,
        renderCell: ({ row }) => formatDateTime(row?.startTime) || "-",
      },
      {
        field: "patientName",
        flex: 0.6,
        minWidth: 210,
        disableColumnMenu: true,
        headerName: "Patient Name",
        renderCell: ({ row }) => (
          <PatientName
            name={row?.patientName}
            gender={row?.patientGender}
            patientId={row?.patientId ?? ""}
            birthDate={row?.patientDob}
          />
        ),
      },
      {
        field: "providerName",
        flex: 1,
        minWidth: 150,
        headerName: "Provider",
        sortable: false,
        renderCell: ({ row }) => (
          <Box>
            <Text isTabletext sx={{ lineHeight: 1.2 }}>
              {row?.providerName ?? "-"}
            </Text>
          </Box>
        ),
      },
      {
        field: "type",
        flex: 1,
        minWidth: 150,
        headerName: "Appointment Type",
        sortable: false,
        renderCell: ({ row }) =>
          row?.type ? capitalize(row?.type.replace(/_/g, " ")) : "-",
      },
      {
        field: "chiefComplaint",
        flex: 1.2,
        minWidth: 150,
        headerName: "Reason for Visit",
        sortable: false,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "pos",
        flex: 0.9,
        minWidth: 150,
        headerName: "Place of Service",
        sortable: false,
        renderCell: ({ row }) => {
          const getPos = () => {
            if (row?.mode === "VIRTUAL") {
              return SUPERBILLPLACEOFSERVICEMAPPER.TELEHEALTH_INSIDE_PATIENT_HOME;
            } else if (row?.mode === "IN_PERSON") {
              return SUPERBILLPLACEOFSERVICEMAPPER.OFFICE;
            } else {
              return "-";
            }
          };

          return <RenderWithTooltip value={getPos()} />;
        },
      },
      {
        field: "paymentMode",
        flex: 0.6,
        minWidth: 160,
        headerName: "Payment Mode",
        sortable: false,
        renderCell: ({ value }) => (value ? value : "-"),
      },
      {
        field: "billedStatus",
        flex: 0.6,
        minWidth: 150,
        headerName: "Billed Status",
        sortable: false,
        renderCell: ({ row }) => {
          const status = row?.billedStatus;
          const label = BILLED_STATUS_MAPPER[status as BilledStatus] ?? "-";
          return <RenderWithTooltip value={label} />;
        },
      },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => (
          <ActionMenu
            options={roleFilter({
              list: [
                { value: "view", label: "View Visit" },
                { value: "createSuperBill", label: "Create Invoice" },
                {
                  value: "createClaim",
                  label: "Create Claim",
                  // Self-Pay and Out of Network visits are billed by invoice
                  // only, so there is no claim to raise for them.
                  isDisabled:
                    row?.paymentMode === "Self Pay" ||
                    row?.paymentMode === "Out of Network" ||
                    row?.paymentMode === "OUT_OF_NETWORK",
                  hoverMsg:
                    "Claims are not applicable for Self-Pay or Out of Network",
                },
              ],
              // "Billed" means every CPT/HCPCS code of the visit is already on a
              // claim or invoice, so nothing is left to bill - hide both creation
              // actions. "Partially Billed" still has unbilled codes, so it keeps them.
              compWith: {
                view: hasView,
                createSuperBill:
                  hasUpdateSuperbill && row?.billedStatus !== "BILLED",
                createClaim: hasUpdateClaims && row?.billedStatus !== "BILLED",
              },
              removeOtherFields: hasReadOnlyEncounterBilling,
            })}
            onActionClick={(v) => {
              if (v === "view") {
                viewEncounterDisclosure.open(row.uuid);
              } else if (v === "createSuperBill") {
                navigate(
                  PROVIDER_PATHS.BILLING +
                    `${
                      row?.uuid
                        ? "invoice/" +
                          row?.patientId +
                          "/" +
                          row?.uuid +
                          (row?.paymentMode
                            ? `?paymentMode=${encodeURIComponent(
                                row.paymentMode
                              )}`
                            : "")
                        : "1"
                    }`
                );
              } else if (v === "createClaim") {
                addClaimsDisclosure.open(row);
              }
            }}
          />
        ),
      },
    ],
    ["patientName", "appointmentDate"]
  );

  return (
    <>
      {viewEncounterDisclosure.opened && viewEncounterDisclosure.data && (
        <ViewEncounterSummaryDrawer
          key={viewEncounterDisclosure.data}
          disclosure={viewEncounterDisclosure}
        />
      )}
      {addClaimsDisclosure.opened && addClaimsDisclosure.data && (
        <CreateClaimDrawer
          disClosureHost={addClaimsDisclosure}
          title="Create Claim"
        />
      )}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={appointmentsList?.data?.content ?? []}
        columns={COLUMNS}
        rowCount={appointmentsList?.data?.page?.totalElements ?? 10}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        loading={isLoading || isFetching}
        rowHeight={BILLING_TABLE_ROW_HEIGHT}
      />
    </>
  );
};

export default ReadyForBillingTab;
